"use client";
import * as React from "react";
import { tv } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { sectionWrapper } from "./sectionWrappers";
import {
  SectionBackgroundColorType,
  SectionTopPaddingSizeType,
  SectionBottomPaddingSizeType,
} from "./types";
import { Checkbox } from "./Checkbox";
import { ErrorIcon } from "./icons/ErrorIcon";
import { DatePicker, DatePickerValue } from "./DatePicker";
import { combineDateAndTime } from "./utils/dateTime";
import { Input } from "./Input";
import { Button } from "./Button";

const locationSearchContainer = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:md:flex-row",
    "uipm:gap-4",
    "uipm:bg-white",
    "uipm:rounded-[var(--radius-lg)]",
    "uipm:items-stretch uipm:md:items-center",
  ),
  variants: {
    embedded: {
      true: "uipm:p-4",
      false: "uipm:py-4 uipm:px-[var(--container-margin)]",
    },
    showDropShadow: {
      true: "uipm:shadow-[0_0_32px_0_rgba(0,0,0,0.10)]",
      false: "",
    },
  },
  defaultVariants: {
    embedded: false,
    showDropShadow: false,
  },
});

const mobileSecondRow = tv({
  base: cn("uipm:flex uipm:gap-4", "uipm:md:contents"),
});

const filterButton = tv({
  base: cn(
    "uipm:relative",
    "uipm:flex uipm:items-center uipm:justify-center",
    "uipm:p-3",
    "uipm:w-[50px] uipm:h-[50px]",
    "uipm:bg-[var(--surface-surface-secondary)]",
    "uipm:rounded-full",
    "uipm:border-0",
    "uipm:cursor-pointer",
    "hover:uipm:bg-[var(--surface-surface-accent-light)]",
    "uipm:transition-colors",
    "uipm:shrink-0",
  ),
  variants: {
    active: {
      true: "before:uipm:content-[''] before:uipm:absolute before:uipm:top-1 before:uipm:right-1 before:uipm:w-3 before:uipm:h-3 before:uipm:bg-[var(--surface-surface-accent)] before:uipm:rounded-full before:uipm:border-2 before:uipm:border-white",
      false: "",
    },
  },
  defaultVariants: {
    active: false,
  },
});

const filterDropdown = tv({
  base: cn(
    "uipm:absolute uipm:top-full uipm:right-0 uipm:mt-7 uipm:z-50",
    "uipm:w-[280px]",
    "uipm:bg-white uipm:rounded-xl uipm:shadow-lg",
    "uipm:border-2 uipm:border-[var(--color-color-light)]",
    "uipm:p-4",
  ),
});

const filterGroup = tv({
  base: cn("uipm:flex uipm:flex-col uipm:gap-3"),
});

const filterGroupTitle = tv({
  base: cn(
    "uipm:text-sm uipm:font-semibold",
    "uipm:text-[var(--typography-colors-text-primary)]",
    "uipm:mb-2",
  ),
});

export interface DestinationInputRenderProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error: boolean;
  onErrorClear: () => void;
  onPlaceSelect: () => void;
}

export interface LocationSearchProps
  extends Omit<ComponentProps<"section">, "id"> {
  destinationPlaceholder?: string;
  datePlaceholder?: string;
  searchButtonLabel?: string;
  filterLabel?: string;
  onSearch?: (data: {
    destination: string;
    startDate: Date | null;
    endDate: Date | null;
    filterApplied: boolean;
    filterType?: string[];
  }) => void;
  onSearchError?: (data: {
    locationInput: string;
    dateSelected: string;
    errorType: string;
    errorMessage: string;
  }) => void;
  onFilterClick?: () => void;
  backgroundColor?: SectionBackgroundColorType;
  topPadding?: SectionTopPaddingSizeType;
  bottomPadding?: SectionBottomPaddingSizeType;
  pageType?: "default" | "location";
  id?: string;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  embedded?: boolean;
  showDropShadow?: boolean;
  renderDestinationInput?: (
    props: DestinationInputRenderProps,
  ) => React.ReactNode;
  getSearchHref?: (data: {
    destination: string;
    startDate: Date | null;
    endDate: Date | null;
    filterApplied: boolean;
    filterType?: string[];
  }) => string;
}

export const LocationSearch: React.FC<LocationSearchProps> = ({
  destinationPlaceholder = "Where are you going?",
  datePlaceholder = "When?",
  filterLabel = "Filter",
  searchButtonLabel = "Find parking",
  onSearch,
  onSearchError,
  onFilterClick,
  backgroundColor = "none",
  topPadding = "default",
  bottomPadding = "default",
  pageType = "default",
  className,
  id,
  dateFormat = "MM/dd/yyyy",
  minDate,
  maxDate,
  embedded = false,
  showDropShadow = false,
  renderDestinationInput,
  getSearchHref,
  ...props
}) => {
  const [destination, setDestination] = React.useState("");
  const [datePickerValue, setDatePickerValue] = React.useState<DatePickerValue>(
    {
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
  );
  const [datePickerOpen, setDatePickerOpen] = React.useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [filters, setFilters] = React.useState({
    parkingTypes: [] as string[],
    amenities: [] as string[],
  });
  const [destinationError, setDestinationError] = React.useState<string>("");
  const [dateError, setDateError] = React.useState<string>("");
  const filterRef = React.useRef<HTMLDivElement>(null);

  const parkingTypeOptions = [
    { id: "street", label: "Street Parking" },
    { id: "garage", label: "Parking Garage" },
    { id: "lot", label: "Parking Lot" },
  ];

  const amenityOptions = [
    { id: "ev", label: "EV Charging" },
    { id: "covered", label: "Covered Parking" },
    { id: "security", label: "24/7 Security" },
    { id: "accessible", label: "Accessible" },
  ];

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFilterToggle = (
    category: "parkingTypes" | "amenities",
    id: string,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(id)
        ? prev[category].filter((item) => item !== id)
        : [...prev[category], id],
    }));
  };

  const handleSearch = () => {
    setDestinationError("");
    setDateError("");

    // Check both validations and collect errors
    let hasError = false;

    if (!destination || destination.trim() === "") {
      const errorMessage = "Please enter a valid location.";
      setDestinationError(errorMessage);
      hasError = true;
    }

    if (!datePickerValue.startDate) {
      const errorMessage = "Please select a date.";
      setDateError(errorMessage);
      hasError = true;
    }

    if (hasError) {
      onSearchError?.({
        locationInput: destination,
        dateSelected: datePickerValue.startDate
          ? datePickerValue.startDate.toISOString().slice(0, 10)
          : "",
        errorType: !destination ? "missing_location" : "missing_date",
        errorMessage: !destination
          ? "Please enter a valid location before searching."
          : "Please select a date before searching.",
      });
      return;
    }

    // TEMPORARILY DISABLED - Filter logic
    // const hasFilters =
    //   filters.parkingTypes.length > 0 || filters.amenities.length > 0;

    // const filterTypes: string[] = [];
    // if (filters.parkingTypes.length > 0) {
    //   filterTypes.push(...filters.parkingTypes);
    // }
    // if (filters.amenities.length > 0) {
    //   filterTypes.push(...filters.amenities);
    // }

    onSearch?.({
      destination,
      startDate: combineDateAndTime(
        datePickerValue.startDate,
        datePickerValue.startTime,
      ),
      endDate: combineDateAndTime(
        datePickerValue.endDate || datePickerValue.startDate,
        datePickerValue.endTime || datePickerValue.startTime,
      ),
      filterApplied: false, // Temporarily disabled
      filterType: undefined, // Temporarily disabled
      // filterApplied: hasFilters,
      // filterType: filterTypes.length > 0 ? filterTypes : undefined,
    });
  };

  const content = (
    <>
      <div
        className={cn(
          locationSearchContainer({ embedded, showDropShadow }),
          className,
        )}
      >
        <div className="uipm:flex uipm:flex-col uipm:flex-1">
          {renderDestinationInput ? (
            renderDestinationInput({
              value: destination,
              onChange: (value) => {
                setDestination(value);
                if (destinationError) setDestinationError("");
              },
              placeholder: destinationPlaceholder,
              error: !!destinationError,
              onErrorClear: () => setDestinationError(""),
              onPlaceSelect: () => setDatePickerOpen(true),
            })
          ) : (
            <Input
              type="text"
              placeholder={destinationPlaceholder}
              value={destination}
              onChange={(e) => {
                setDestination(e.target.value);
                if (destinationError) setDestinationError("");
              }}
              state={destinationError ? "error" : "default"}
              background="default"
            />
          )}
          {destinationError && (
            <span className="uipm:flex uipm:items-center uipm:gap-2 uipm:mt-1">
              <ErrorIcon size={24} />
              <span className="uipm:text-[var(--color-color-danger)] uipm:text-sm">
                {destinationError}
              </span>
            </span>
          )}
        </div>

        <div className={mobileSecondRow()}>
          <div
            className={cn(
              "uipm:w-full uipm:md:w-auto uipm:min-w-0",
              "uipm:flex uipm:flex-col uipm:flex-1",
            )}
          >
            <DatePicker
              selectsRange
              showTimeSelect
              open={datePickerOpen}
              onOpenChange={setDatePickerOpen}
              onChange={(value: DatePickerValue) => {
                setDatePickerValue(value);
                if (dateError) setDateError("");
              }}
              dateFormat={dateFormat}
              minDate={minDate || new Date()}
              maxDate={maxDate}
              placeholderText={datePlaceholder}
              state={dateError ? "error" : "default"}
              background="default"
              containerClassName="uipm:w-full"
              popperClassName={"uipm:mt-0"}
            />
            {dateError && (
              <span className="uipm:flex uipm:items-center uipm:gap-2 uipm:mt-1">
                <ErrorIcon size={24} />
                <span className="uipm:text-[var(--color-color-danger)] uipm:text-sm">
                  {dateError}
                </span>
              </span>
            )}
          </div>

          {/* TEMPORARILY DISABLED - Filter dropdown */}
          {/* <div
            className="uipm:relative uipm:shrink-0 uipm:mt-[3px] uipm:md:mt-0"
            ref={filterRef}
          >
            <button
              type="button"
              className={filterButton({
                active:
                  filters.parkingTypes.length > 0 ||
                  filters.amenities.length > 0,
              })}
              onClick={() => {
                setFilterOpen(!filterOpen);
                onFilterClick?.();
              }}
              aria-label={filterLabel}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M14 9.64961C14.9113 9.64961 15.65 8.91088 15.65 7.99961C15.65 7.08834 14.9113 6.34961 14 6.34961C13.0888 6.34961 12.35 7.08834 12.35 7.99961C12.35 8.91088 13.0888 9.64961 14 9.64961ZM17.15 7.99961C17.15 9.73931 15.7397 11.1496 14 11.1496C12.2604 11.1496 10.85 9.73931 10.85 7.99961C10.85 6.25991 12.2604 4.84961 14 4.84961C15.7397 4.84961 17.15 6.25991 17.15 7.99961ZM10 17.6496C9.08873 17.6496 8.35 16.9109 8.35 15.9996C8.35 15.0883 9.08873 14.3496 10 14.3496C10.9113 14.3496 11.65 15.0883 11.65 15.9996C11.65 16.9109 10.9113 17.6496 10 17.6496ZM6.85 15.9996C6.85 17.7393 8.2603 19.1496 10 19.1496C11.7397 19.1496 13.15 17.7393 13.15 15.9996C13.15 14.2599 11.7397 12.8496 10 12.8496C8.2603 12.8496 6.85 14.2599 6.85 15.9996ZM13.65 15.9665C13.65 15.5523 13.9858 15.2165 14.4 15.2165H20C20.4143 15.2165 20.75 15.5523 20.75 15.9665C20.75 16.3807 20.4143 16.7165 20 16.7165H14.4C13.9858 16.7165 13.65 16.3807 13.65 15.9665ZM9.6 7.2165C10.0142 7.2165 10.35 7.55229 10.35 7.9665C10.35 8.38072 10.0142 8.7165 9.6 8.7165L4 8.7165C3.58579 8.7165 3.25 8.38072 3.25 7.9665C3.25 7.55229 3.58579 7.2165 4 7.2165L9.6 7.2165ZM3.25002 15.9665C3.25002 15.5523 3.58581 15.2165 4.00002 15.2165H5.60002C6.01424 15.2165 6.35002 15.5523 6.35002 15.9665C6.35002 16.3807 6.01424 16.7165 5.60002 16.7165H4.00002C3.58581 16.7165 3.25002 16.3807 3.25002 15.9665ZM20 7.2165C20.4142 7.2165 20.75 7.55229 20.75 7.9665C20.75 8.38072 20.4142 8.7165 20 8.7165L18.4 8.7165C17.9858 8.7165 17.65 8.38072 17.65 7.9665C17.65 7.55229 17.9858 7.2165 18.4 7.2165L20 7.2165Z"
                  fill="#38724E"
                />
              </svg>
            </button>

            {filterOpen && (
              <div className={filterDropdown()}>
                <div className={filterGroup()}>
                  <div className={filterGroupTitle()}>Parking Type</div>
                  {parkingTypeOptions.map((option) => (
                    <Checkbox
                      key={option.id}
                      label={option.label}
                      checked={filters.parkingTypes.includes(option.id)}
                      onChange={() =>
                        handleFilterToggle("parkingTypes", option.id)
                      }
                    />
                  ))}
                </div>

                <div className={cn(filterGroup(), "uipm:mt-4")}>
                  <div className={filterGroupTitle()}>Amenities</div>
                  {amenityOptions.map((option) => (
                    <Checkbox
                      key={option.id}
                      label={option.label}
                      checked={filters.amenities.includes(option.id)}
                      onChange={() =>
                        handleFilterToggle("amenities", option.id)
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div> */}
        </div>

        {(() => {
          const isFormValid =
            destination.trim() !== "" && datePickerValue.startDate !== null;
          const searchData = {
            destination,
            startDate: combineDateAndTime(
              datePickerValue.startDate,
              datePickerValue.startTime,
            ),
            endDate: combineDateAndTime(
              datePickerValue.endDate || datePickerValue.startDate,
              datePickerValue.endTime || datePickerValue.startTime,
            ),
            // TEMPORARILY DISABLED - Filter logic
            filterApplied: false,
            // filterApplied:
            //   filters.parkingTypes.length > 0 || filters.amenities.length > 0,
            filterType: undefined,
            // filterType:
            //   [...filters.parkingTypes, ...filters.amenities].length > 0
            //     ? [...filters.parkingTypes, ...filters.amenities]
            //     : undefined,
          };

          if (getSearchHref && isFormValid) {
            return (
              <Button
                href={getSearchHref(searchData)}
                variant="primary"
                onClick={() => onSearch?.(searchData)}
                className="uipm:w-full uipm:md:w-auto"
              >
                {searchButtonLabel}
              </Button>
            );
          }

          return (
            <Button
              type="button"
              variant="primary"
              onClick={handleSearch}
              className="uipm:w-full uipm:md:w-auto"
            >
              {searchButtonLabel}
            </Button>
          );
        })()}
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <section
      id={id}
      className={cn(
        sectionWrapper({
          backgroundColor,
          topPadding,
          bottomPadding,
          pageType,
        }),
        "uipm:overflow-visible",
      )}
      {...props}
    >
      {content}
    </section>
  );
};
